/**
 * mr-enhancements.js — Sprint 6 Phase 3
 *
 * Purpose: wire mr-c-enhancement's checkboxes so a checked box actually
 * submits the correct Infinite Options (IO) line-item property at
 * Add-to-Cart, and show a running total that is, by construction,
 * identical to what IO will charge.
 *
 * ARCHITECTURE (Decision 1, MR-Sprint6-Decisions-and-Implementation-Plan.md):
 * "Enhancements ride entirely on Infinite Options' existing paid
 * line-item-property mechanism. No new variant, no separate app, no
 * custom cart line. mr_enhancement.price is a display value only — the
 * real charge is whatever IO's own paid-option config already enforces
 * app-side." Concretely: each mr_enhancement catalog entry's
 * `io_field_mapping` value IS the exact property key IO's own pricing
 * config listens for. mr-c-enhancement.liquid (Phase 2) renders the
 * checkbox WITHOUT a `name`/`value` yet — deliberately, since Phase 2 is
 * a "dumb renderer" with zero business logic (Component Inventory §2).
 * This script's only job is to complete that wiring client-side:
 *   checkbox.name  = 'properties[' + data-io-field + ']'
 *   checkbox.value = <the enhancement's own display name>
 * so the product form submits exactly the property IO expects — the
 * same outcome as if IO's own (differently styled) checkbox had been
 * checked directly.
 *
 * NO MUTATION OF INFINITE OPTIONS STATE: this script never touches any
 * element outside `.mr-c-enhancement` — no IO input, checkbox, or hidden
 * field is read, clicked, or written anywhere. It only sets attributes
 * on MR's OWN checkboxes (which Phase 2 already renders inert/unwired
 * on purpose). This is completing MR's own markup, not reaching into
 * IO's — consistent with mr-dedication.js's read-only rule, applied to
 * the one component in Sprint 6 that is explicitly documented as an
 * "IO-paid-property wrapper" rather than a pure read-only observer.
 *
 * PRICE-TOTAL / IO-CONSISTENCY: `mr_enhancement.price` is asserted by
 * Decision 1 to equal whatever IO's own config actually charges for
 * that `io_field_mapping` — keeping the two in sync is an Operations/
 * content-QA responsibility, not something this script can verify at
 * runtime. Given that contract, "total matches the actual IO-enforced
 * charge" is achieved BY CONSTRUCTION here: this script never invents
 * its own number, it only sums the exact `data-price` values Liquid
 * already rendered from `mr_enhancement.price` — the same source of
 * truth IO's charge is asserted to match.
 *
 * CURRENCY FORMATTING: no currency symbol, locale, or decimal
 * convention is hardcoded anywhere in this file. The total's display
 * format is derived from an already server-rendered
 * `.mr-c-enhancement__price` string on the same page (Liquid's own
 * `money` filter output, e.g. "+Dhs. 4.00"), so the total always
 * matches whatever currency/format the shop and Shopify Markets are
 * already showing everywhere else on the page.
 *
 * REVEAL-WITHOUT-SHIFT: the total row element is created once, at init,
 * before any user interaction — later checkbox toggles only change its
 * text content and a visibility class, never insert/remove DOM nodes.
 * Same "reserve space at init, class-toggle only" pattern as
 * mr-c-dedication (Phase 2), and its CSS rule likewise carries no
 * `transition` property — instant reveal only, no animation flourish.
 *
 * No scroll-event listeners. Progressive enhancement: if this script
 * fails to load, the checkboxes stay exactly as Phase 2 shipped them —
 * inert, unwired, no property submitted, no charge. That is the safe
 * failure direction (never silently charges for something the customer
 * never saw confirmed), matching mr-c-enhancement's own
 * "step-hides-when-empty" safety-first philosophy.
 *
 * SCOPE NOTE: for `properties[...]` to actually reach the cart, this
 * fieldset must be rendered inside the product's own add-to-cart
 * `<form>` — that placement is Phase 4's job (section composition,
 * locked block order: tier picker -> enhancements -> ... -> buy
 * buttons, all inside mr-s-product-commitment). This script does not
 * create or move any form; it only wires the inputs so they behave
 * correctly once Phase 4 places them inside one.
 */
(function () {
  'use strict';

  var FIELDSET_SELECTOR = '.mr-c-enhancement';
  var CHECKBOX_SELECTOR = '[data-mr-enhancement]';
  var TOTAL_CLASS = 'mr-c-enhancement__total';
  var TOTAL_VISIBLE_CLASS = 'mr-c-enhancement__total--visible';

  function parsePrice(str) {
    var n = parseFloat(str);
    return isFinite(n) ? n : 0;
  }

  function wireCheckbox(checkbox) {
    var ioField = checkbox.getAttribute('data-io-field');
    if (!ioField) return; // no catalog mapping — leave inert, safe no-op
    var item = checkbox.closest('.mr-c-enhancement__item');
    var nameEl = item && item.querySelector('.mr-c-enhancement__name');
    var readableValue = (nameEl && nameEl.textContent.trim()) || ioField;
    checkbox.name = 'properties[' + ioField + ']';
    checkbox.value = readableValue;
  }

  function deriveMoneyTemplate(fieldset) {
    var priceEl = fieldset.querySelector('.mr-c-enhancement__price');
    if (!priceEl) return null; // no priced items on this product — nothing to total
    var text = priceEl.textContent || '';
    // Find the actual digit run (must START with a digit) rather than a
    // lazy "everything before the first period-like char" match — the
    // latter incorrectly treats a currency abbreviation's own period
    // (e.g. "Dhs.") as if it were part of the number.
    var match = text.match(/\d[\d,]*(\.\d+)?/);
    if (!match) return null;
    var numberStr = match[0];
    var index = match.index;
    var prefix = text.slice(0, index).replace(/^\s*\+\s*/, ''); // strip the per-row "+" — this is a total, not an addition
    var suffix = text.slice(index + numberStr.length);
    var usesCommaDecimal = /,\d{1,2}$/.test(numberStr) && numberStr.indexOf('.') === -1;
    return { prefix: prefix, suffix: suffix, usesCommaDecimal: usesCommaDecimal };
  }

  function formatTotal(total, template) {
    var formatted = total.toFixed(2);
    if (template.usesCommaDecimal) formatted = formatted.replace('.', ',');
    return template.prefix + formatted + template.suffix;
  }

  function createTotalRow(fieldset) {
    var row = document.createElement('div');
    row.className = TOTAL_CLASS;
    row.setAttribute('data-mr-enhancement-total', '');
    row.setAttribute('aria-live', 'polite');

    var label = document.createElement('span');
    label.className = 'mr-c-enhancement__total-label';
    label.textContent = 'Enhancements total';

    var value = document.createElement('span');
    value.className = 'mr-c-enhancement__total-value';

    row.appendChild(label);
    row.appendChild(value);
    fieldset.appendChild(row);

    return { row: row, value: value };
  }

  function initFieldset(fieldset) {
    var checkboxes = Array.prototype.slice.call(fieldset.querySelectorAll(CHECKBOX_SELECTOR));
    if (checkboxes.length === 0) return;

    checkboxes.forEach(wireCheckbox);

    var template = deriveMoneyTemplate(fieldset);
    if (!template) return; // nothing priced — no total row, no shift risk either way

    var totalParts = createTotalRow(fieldset);

    function sync() {
      var total = 0;
      checkboxes.forEach(function (cb) {
        if (cb.checked) total += parsePrice(cb.getAttribute('data-price'));
      });

      if (total > 0) {
        totalParts.value.textContent = formatTotal(total, template);
        totalParts.row.classList.add(TOTAL_VISIBLE_CLASS);
      } else {
        totalParts.value.textContent = '';
        totalParts.row.classList.remove(TOTAL_VISIBLE_CLASS);
      }
    }

    checkboxes.forEach(function (cb) {
      cb.addEventListener('change', sync);
    });

    sync(); // reflect any pre-checked state (e.g. bfcache restore) without shift
  }

  function init(root) {
    var scope = root || document;
    var fieldsets = scope.querySelectorAll(FIELDSET_SELECTOR);
    fieldsets.forEach(function (fieldset) {
      initFieldset(fieldset);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Exposed for Phase 4 re-init (variant change swapping the fieldset) and
  // for QA-harness isolated testing.
  window.MREnhancements = { init: init };
})();
